/**
 * #modules/api/Banking.js
 * Hooks and utilities for working with the Banking API.
 */
import React from 'react';
import { toast } from 'react-toastify';
import { useIntl } from 'react-intl';
import useLiquidNobleApi from '#modules/utils/useLiquidNobleApi';
import { BankingContext } from '#modules/context/BankingContext';
import { EntityContext } from '#modules/context/EntityContext';
import { useSelectedProfileId } from '#modules/state/ProfileState';
import { errorResponseNotify } from '#modules/utils/errors';
import { useStaticResources } from '@tokenized/sdk-react-private';
import { normalizeValue } from '#modules/api/User';
import { getSessionUnlockingKey } from '#modules/utils/jwt';
import usePasskey from '#modules/hooks/usePasskey';

export const INSTRUMENT_TYPE_COMMODITY = 1;
export const INSTRUMENT_TYPE_FIAT = 2;
export const INSTRUMENT_TYPE_CRYPTO = 3;
export const INSTRUMENT_TYPE_SECURITY = 4;

export const TRADE_CATEGORY_BSV_TOPUP = 1;
export const TRADE_CATEGORY_METAL_ORDER = 2;

export const METAL_ORDER_EXPIRY_TIME = 24; // Hours

export const ORDER_STATUS_PENDING = 1;
export const ORDER_STATUS_REJECTED = 6;
export const ORDER_STATUS_FINALIZED = 7;
export const ORDER_STATUS_MANUAL_REVIEW = 8;

export const ORDER_TYPE_BUY = 1;
export const ORDER_TYPE_SELL = 2;
export const ORDER_TYPE_REDEMPTION = 3;
export const ORDER_TYPE_TRANSFER = 4;
export const ORDER_TYPE_SWAP = 5;
export const ORDER_TYPE_PAYMENT_REQUEST = 6;

export const PLAN_TIME_RULE_INTERVAL = 'interval';
export const PLAN_TIME_RULE_DEPOSIT_TRIGGERED = 'deposit_triggered';

export let SUPPORTED_CURRENCY_CODES;
let supportedCurrencyCodesPromise;

let CURRENCIES_LIST;
let CURRENCIES_BY_CODE;
export let INSTRUMENTS;
let instrumentsPromise;

export const useCurrenciesList = () => {
  const currencies = useStaticResources()?.currencies;
  if (CURRENCIES_LIST || !currencies) {
    // only need to do this once (it’s static data)
    return [CURRENCIES_LIST, CURRENCIES_BY_CODE];
  }

  CURRENCIES_LIST = [...currencies]
    .map(([code, { label: name }]) => ({ code, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
  CURRENCIES_BY_CODE = new Map(
    CURRENCIES_LIST.map((item) => [item.code, item]),
  );
  return [CURRENCIES_LIST, CURRENCIES_BY_CODE];
};

export const useBankAccounts = () => {
  const liquidNobleApi = useLiquidNobleApi();
  const { bankAccounts, setBankAccounts, loading, setLoading } =
    React.useContext(BankingContext);
  const { activeEntityId, entityInfo } = React.useContext(EntityContext);
  const entity = entityInfo?.[activeEntityId];
  const isRestricted =
    entity === undefined ? true : entity?.restricted === true;
  const refetch = React.useCallback(async () => {
    if (loading || !activeEntityId) {
      return;
    }
    setLoading(true);
    try {
      const response = await liquidNobleApi.get('/banking/accounts/', {
        params: {
          entity_id: activeEntityId,
        },
      });

      const { data } = response;
      setBankAccounts(data?.accounts ?? []);
    } catch (err) {
      console.error('API error: ', err);
      setBankAccounts([]);
    }
    setLoading(false);
  }, [liquidNobleApi, setBankAccounts, setLoading, loading, activeEntityId]);

  React.useEffect(() => {
    async function loadBankAccounts() {
      refetch();
    }
    if (!isRestricted) {
      loadBankAccounts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liquidNobleApi, activeEntityId]);
  return [bankAccounts, refetch, loading, isRestricted];
};

export const useBalanceStatements = (lockboxId) => {
  const liquidNobleApi = useLiquidNobleApi();
  const [loading, setLoading] = React.useState(false);
  const [balanceStatements, setBalanceStatements] = React.useState([]);
  const { activeEntityId } = React.useContext(EntityContext);
  const profileId = useSelectedProfileId();
  const refetch = React.useCallback(async () => {
    if (loading || !activeEntityId || !lockboxId) {
      return;
    }
    setLoading(true);
    try {
      const response = await liquidNobleApi.get('/banking/statements/', {
        params: {
          entity_id: activeEntityId,
          profile_id: profileId,
          lockbox_id: lockboxId,
        },
      });

      const { data } = response;
      setBalanceStatements(data?.statements ?? []);
    } catch (err) {
      console.error('API error: ', err);
      setBalanceStatements([]);
    }
    setLoading(false);
  }, [
    liquidNobleApi,
    setBalanceStatements,
    setLoading,
    loading,
    activeEntityId,
    profileId,
    lockboxId,
  ]);

  React.useEffect(() => {
    async function loadBalanceStatements() {
      refetch();
    }
    loadBalanceStatements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liquidNobleApi, activeEntityId, lockboxId]);
  return [balanceStatements, refetch, loading];
};

export const useBeneficiaryFields = (currency_code) => {
  const [beneficiaryFields, setBeneficiaryFields] = React.useState(null);
  const liquidNobleApi = useLiquidNobleApi();
  React.useEffect(() => {
    async function loadAccountFields() {
      if (!currency_code) {
        return;
      }
      const response = await liquidNobleApi.get('/banking/beneficiary/fields', {
        params: {
          currency_code: currency_code,
        },
      });

      const { data } = response;
      if (data) {
        setBeneficiaryFields(data);
      }
    }
    if (currency_code) {
      loadAccountFields();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currency_code]);
  return beneficiaryFields;
};

export const useServiceAccountCounterparty = () => {
  const [serviceAccountCounterparty, setServiceAccountCounterparty] =
    React.useState(null);
  const liquidNobleApi = useLiquidNobleApi();
  React.useEffect(() => {
    async function loadServiceAccountCounterparty() {
      const response = await liquidNobleApi.get('/banking/topup/counterparty/');

      const { data } = response;
      if (data?.display_handle) {
        const normalizedData = normalizeValue(data);
        setServiceAccountCounterparty({
          ...data,
          ...normalizedData,
        });
      }
    }
    loadServiceAccountCounterparty();
  }, [liquidNobleApi]);
  return serviceAccountCounterparty;
};

export const useInstruments = () => {
  const [instruments, setInstruments] = React.useState(null);
  const liquidNobleApi = useLiquidNobleApi();
  React.useEffect(() => {
    async function loadInstruments() {
      if (instrumentsPromise || INSTRUMENTS !== undefined) {
        return instrumentsPromise;
      }
      instrumentsPromise = liquidNobleApi.get('/banking/instruments');
      const response = await instrumentsPromise;
      console.log('API loaded instruments: ', response);

      const { data } = response;
      if (data) {
        INSTRUMENTS = data;
      }
      instrumentsPromise = null;
    }
    async function initInstruments() {
      await loadInstruments();
      if (INSTRUMENTS !== undefined) {
        setInstruments(INSTRUMENTS);
      }
    }
    initInstruments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return instruments;
};

export const useSupportedCurrencyCodes = () => {
  const [supportedCurrencyCodes, setSupportedCurrencyCodes] = React.useState(
    [],
  );
  const liquidNobleApi = useLiquidNobleApi();
  React.useEffect(() => {
    async function loadSupportedCurrencyCodes() {
      if (
        supportedCurrencyCodesPromise ||
        SUPPORTED_CURRENCY_CODES !== undefined
      ) {
        return supportedCurrencyCodesPromise;
      }
      supportedCurrencyCodesPromise = liquidNobleApi.get('/banking/currencies');
      const response = await supportedCurrencyCodesPromise;
      console.log('API loaded supported currency codes: ', response);

      const { data } = response;
      if (data?.currency_codes) {
        SUPPORTED_CURRENCY_CODES = data.currency_codes;
      }
      supportedCurrencyCodesPromise = null;
    }
    async function initSupportedCurrencyCodes() {
      await loadSupportedCurrencyCodes();
      if (SUPPORTED_CURRENCY_CODES !== undefined) {
        setSupportedCurrencyCodes(SUPPORTED_CURRENCY_CODES);
      }
    }
    initSupportedCurrencyCodes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return supportedCurrencyCodes;
};

export const useBsvTopupOptions = () => {
  const [bsvTopupOptions, setBsvTopupOptions] = React.useState(null);
  const liquidNobleApi = useLiquidNobleApi();
  React.useEffect(() => {
    async function loadBsvTopupOptions() {
      const response = await liquidNobleApi.get('/service/bsv/options/');

      const { data } = response;
      if (data?.options) {
        setBsvTopupOptions(data.options);
      }
    }
    loadBsvTopupOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return bsvTopupOptions;
};

export const useInstrumentTypes = (instrumentTypes) => {
  const instruments = useInstruments();
  return instruments?.filter((instrument) =>
    instrumentTypes.includes(instrument.instrument_type),
  );
};

export const useTransferableInstruments = ({ excludeCrypto = false } = {}) => {
  const instruments = useInstruments();
  return instruments?.filter(
    (instrument) =>
      (instrument.is_transferable_external ||
        instrument.is_transferable_personal) &&
      (!excludeCrypto || instrument.instrument_type !== INSTRUMENT_TYPE_CRYPTO),
  );
};

export const useDepositableInstruments = () => {
  const instruments = useInstruments();
  return instruments?.filter((instrument) => instrument.can_deposit_external);
};

export const useCommodityInstruments = () => {
  return useInstrumentTypes([INSTRUMENT_TYPE_COMMODITY]);
};

export const useFiatInstruments = () => {
  return useInstrumentTypes([INSTRUMENT_TYPE_FIAT]);
};

export const useCryptoInstruments = () => {
  return useInstrumentTypes([INSTRUMENT_TYPE_CRYPTO]);
};

export const useCryptoAndFiatInstruments = () => {
  return useInstrumentTypes([INSTRUMENT_TYPE_CRYPTO, INSTRUMENT_TYPE_FIAT]);
};

export const useSecurityInstruments = () => {
  return useInstrumentTypes([INSTRUMENT_TYPE_SECURITY]);
};

export const useVisibleInstruments = () => {
  return useInstrumentTypes([
    INSTRUMENT_TYPE_COMMODITY,
    INSTRUMENT_TYPE_FIAT,
    INSTRUMENT_TYPE_SECURITY,
    INSTRUMENT_TYPE_CRYPTO,
  ]);
};

export const usePaymentInstruments = (showCrypto = false) => {
  const paymentInstruments = useInstrumentTypes([
    INSTRUMENT_TYPE_FIAT,
    ...(showCrypto ? [INSTRUMENT_TYPE_CRYPTO] : []),
  ]);
  return (
    paymentInstruments?.filter((instrument) => instrument.is_payment_method) ??
    []
  );
};

export const useOrderPrice = ({
  quantity,
  orderType,
  orderInstrumentId,
  paymentInstrumentId,
  bankAccountId,
  entityId,
  quoteAgainstPaymentAmount,
  counterpartyHandle,
  payingByCard,
  existingQuoteId,
}) => {
  const [orderPrice, setOrderPrice] = React.useState(null);
  const liquidNobleApi = useLiquidNobleApi();
  const { displayCurrencyCode } = React.useContext(BankingContext);
  React.useEffect(() => {
    async function loadOrderPrice() {
      let response = {};
      if (!entityId) {
        return;
      }
      try {
        response = await liquidNobleApi.post('/order/price/', {
          quantity,
          order_instrument_id: orderInstrumentId,
          payment_instrument_id: paymentInstrumentId,
          bank_account_id: bankAccountId,
          entity_id: entityId,
          quote_against_payment_amount: quoteAgainstPaymentAmount,
          order_type: orderType,
          counterparty_handle: counterpartyHandle,
          card_payment: payingByCard,
          display_currency_code: displayCurrencyCode,
        });
      } catch (error) {
        const errorMessage = errorResponseNotify(error);
        setOrderPrice({
          error: {
            message: errorMessage,
            reason: error?.data?.reason,
          },
        });
        return;
      }

      const { data } = response;
      if (data?.price) {
        setOrderPrice({
          price: data.price,
          fees: data.fees,
          tradedInstrumentId: data.traded_instrument_id,
          feesInstrumentId: data.fees_instrument_id,
          quantity: data.quantity,
          quoteId: data.quote_id,
          stripeIntentSecret: data.stripe_intent_secret,
          stripeReturnUrl: data.stripe_return_url,
          quoteAgainstPaymentAmount,
          ...data,
        });
      } else if (data?.error) {
        toast.error(data.error);
        setOrderPrice({
          error: data.error,
        });
      }
    }

    if (
      [ORDER_TYPE_BUY, ORDER_TYPE_SELL, ORDER_TYPE_REDEMPTION].includes(
        orderType,
      )
    ) {
      if (
        !orderInstrumentId ||
        (!paymentInstrumentId && !bankAccountId && !payingByCard) ||
        !quantity
      ) {
        return;
      }
    }
    if ([ORDER_TYPE_SWAP, ORDER_TYPE_PAYMENT_REQUEST].includes(orderType)) {
      // TODO: implement me
      return;
    }
    if (orderType === ORDER_TYPE_TRANSFER) {
      if (!paymentInstrumentId || !quantity) {
        return;
      }
    }

    if (existingQuoteId) {
      console.log(
        'Skip loading order price, using existing quote: ',
        existingQuoteId,
      );
      return;
    }

    if (
      [ORDER_TYPE_BUY, ORDER_TYPE_SELL, ORDER_TYPE_REDEMPTION].includes(
        orderType,
      ) &&
      orderPrice?.quoteAgainstPaymentAmount &&
      orderPrice?.quoteAgainstPaymentAmount === quoteAgainstPaymentAmount &&
      orderPrice?.tradedInstrumentId === orderInstrumentId
    ) {
      console.log(
        'Skip loading order price, quote against payment amount is the same',
      );
      return;
    }

    loadOrderPrice();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    quantity,
    orderType,
    orderInstrumentId,
    paymentInstrumentId,
    bankAccountId,
    payingByCard,
    existingQuoteId,
    quoteAgainstPaymentAmount,
  ]);
  return orderPrice;
};

export const useOrderQuote = ({ quoteId, entityId }) => {
  const [orderQuote, setOrderQuote] = React.useState(null);
  const liquidNobleApi = useLiquidNobleApi();
  React.useEffect(() => {
    async function loadOrderQuote() {
      let response = {};
      if (!quoteId || !entityId) {
        return;
      }
      try {
        response = await liquidNobleApi.post('/order/quote/', {
          quote_id: quoteId,
          entity_id: entityId,
        });
      } catch (error) {
        const errorMessage = errorResponseNotify(error);
        setOrderQuote({
          error: errorMessage,
        });
        return;
      }

      const { data } = response;
      if (data?.price) {
        setOrderQuote({
          price: data.price,
          fees: data.fees,
          tradedInstrumentId: data.traded_instrument_id,
          feesInstrumentId: data.fees_instrument_id,
          quantity: data.quantity,
          quoteId: data.quote_id,
          stripeIntentSecret: data.stripe_intent_secret,
          stripeReturnUrl: data.stripe_return_url,
        });
      } else if (data?.error) {
        toast.error(data.error);
        setOrderQuote({
          error: data.error,
        });
      }
    }
    loadOrderQuote();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteId, entityId]);
  return orderQuote;
};

export const useSubmitBuyOrder = () => {
  const liquidNobleApi = useLiquidNobleApi();
  const { getPasskeyAssertion } = usePasskey();

  const intl = useIntl();
  return async ({
    activity_id,
    buy_instrument_id,
    amount,
    pay_with,
    payment_amount,
    entity_id,
    profile_id,
    quote_id,
    mfa_code,
    stripe_payment_intent_id,
    onSuccess,
    onError,
  }) => {
    let assertionResponse = null;
    if (!stripe_payment_intent_id) {
      assertionResponse = await getPasskeyAssertion();
    }
    const unlocking_key = getSessionUnlockingKey();
    const postData = {
      amount,
      buy_instrument_id,
      entity_id,
      profile_id,
      pay_with,
      activity_id,
      payment_amount,
      quote_id,
      mfa_code,
      unlocking_key,
      stripe_payment_intent_id,
      passkey_assertion: assertionResponse,
    };
    try {
      const response = await liquidNobleApi.post('/order/buy/', postData);
      const { data } = response;
      const onSuccessMessage = intl.formatMessage({
        defaultMessage: 'Request submitted successfully',
        description: 'Successful order request toast message',
      });
      if (data?.success) {
        toast.success(onSuccessMessage);
      }
      onSuccess?.(data);
    } catch (error) {
      errorResponseNotify(error);
      onError?.(error);
    }
  };
};

export const useSubmitSellOrder = () => {
  const liquidNobleApi = useLiquidNobleApi();
  const { getPasskeyAssertion } = usePasskey();
  const intl = useIntl();
  return async ({
    endpoint,
    activity_id,
    sell_instrument_id,
    proceeds_instrument_id,
    amount,
    proceeds_amount,
    entity_id,
    profile_id,
    quote_id,
    mfa_code,
    onSuccess,
    onError,
    ...rest
  }) => {
    const assertionResponse = await getPasskeyAssertion();
    const unlocking_key = getSessionUnlockingKey();
    const postData = {
      amount,
      sell_instrument_id,
      proceeds_instrument_id,
      entity_id,
      profile_id,
      activity_id,
      proceeds_amount,
      quote_id,
      mfa_code,
      unlocking_key,
      passkey_assertion: assertionResponse,
      ...rest,
    };
    const isWithdrawal = !!rest?.withdraw_instrument_id;
    try {
      const response = await liquidNobleApi.post(endpoint, postData);
      const { data } = response;
      const onSuccessMessage = isWithdrawal
        ? intl.formatMessage({
            defaultMessage: 'Withdrawal request submitted successfully',
            description: 'Successful order request toast message',
          })
        : intl.formatMessage({
            defaultMessage: 'Sell request submitted successfully',
            description: 'Successful request toast message',
          });
      if (data?.success) {
        toast.success(onSuccessMessage);
      }
      onSuccess?.(data);
    } catch (error) {
      errorResponseNotify(error);
      onError?.(error);
    }
  };
};

export const useSubmitTransferOrder = () => {
  const liquidNobleApi = useLiquidNobleApi();
  const { getPasskeyAssertion } = usePasskey();
  const intl = useIntl();
  return async ({
    endpoint,
    activity_id,
    quote_id,
    entity_id,
    profile_id,
    mfa_code,
    onSuccess,
    onError,
    ...rest
  }) => {
    const assertionResponse = await getPasskeyAssertion();
    const unlocking_key = getSessionUnlockingKey();
    const postData = {
      activity_id,
      quote_id,
      entity_id,
      profile_id,
      mfa_code,
      unlocking_key,
      passkey_assertion: assertionResponse,
      ...rest,
    };
    try {
      const response = await liquidNobleApi.post(endpoint, postData);
      const { data } = response;
      const onSuccessMessage = intl.formatMessage({
        defaultMessage: 'Transfer confirmed',
        description: 'Successful transfer request toast message',
      });
      if (data?.success) {
        toast.success(onSuccessMessage);
      }
      onSuccess?.(data);
    } catch (error) {
      errorResponseNotify(error);
      onError?.(error);
    }
  };
};

export const useFetchOrderStatus = () => {
  const liquidNobleApi = useLiquidNobleApi();
  return React.useCallback(
    async ({ entityId, orderId, orderType, onSuccess, onError }) => {
      try {
        const response = await liquidNobleApi.get('/order/status/', {
          params: {
            order_id: orderId,
            entity_id: entityId,
            order_type: orderType,
          },
        });
        const { data } = response;
        onSuccess?.(data);
      } catch (error) {
        errorResponseNotify(error);
        onError?.(error);
      }
    },
    [liquidNobleApi],
  );
};

export const useRetrievePriceHistory = () => {
  const liquidNobleApi = useLiquidNobleApi();

  const retrievePriceHistory = React.useCallback(
    async ({ instrumentId, dateFrom, dateTo }) => {
      if (!instrumentId) {
        console.log('useRetrievePriceHistory failed: No instrumentId provided');
        return;
      }
      // Ensure dateFrom and dateTo are a unix timestamp
      if (dateFrom && typeof dateFrom !== 'number') {
        dateFrom = new Date(dateFrom).getTime() / 1000;
      } else if (dateFrom) {
        dateFrom = Math.floor(dateFrom / 1000);
      }
      if (dateTo && typeof dateTo !== 'number') {
        dateTo = new Date(dateTo).getTime() / 1000;
      } else if (dateTo) {
        dateTo = Math.floor(dateTo / 1000);
      }
      try {
        const response = await liquidNobleApi.get(
          `/banking/instrument/history/?instrument_id=${instrumentId}&date_from=${dateFrom}&date_to=${dateTo}`,
        );
        const { data } = response;
        return data?.data ?? [];
      } catch (err) {
        errorResponseNotify(err);
        return null;
      }
    },
    [liquidNobleApi],
  );

  return retrievePriceHistory;
};

export const useOrderStatusLabel = (status) => {
  const intl = useIntl();
  switch (status) {
    case ORDER_STATUS_PENDING:
      return intl.formatMessage({
        defaultMessage: 'Processing',
        description: 'Order status processing',
      });
    case ORDER_STATUS_REJECTED:
      return intl.formatMessage({
        defaultMessage: 'Rejected',
        description: 'Order status rejected',
      });
    case ORDER_STATUS_FINALIZED:
      return intl.formatMessage({
        defaultMessage: 'Finalized',
        description: 'Order status finalized',
      });
    case ORDER_STATUS_MANUAL_REVIEW:
      return intl.formatMessage({
        defaultMessage: 'Being reviewed',
        description: 'Order status manual review',
      });
    default:
      return '';
  }
};

export const useCgtReport = () => {
  const liquidNobleApi = useLiquidNobleApi();
  const { activeEntityId } = React.useContext(EntityContext);

  const downloadCgtReport = React.useCallback(
    async (taxYear = null) => {
      const params = taxYear != null ? { tax_year: taxYear } : {};
      const response = await liquidNobleApi.get(
        `/entity/${activeEntityId}/cgt-report/`,
        { responseType: 'arraybuffer', params },
      );

      const disposition = response.headers?.['content-disposition'] ?? '';
      const match = disposition.match(/filename="?([^"]+)"?/);
      const fileName = match?.[1] ?? 'cgt-report.xlsx';

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      // eslint-disable-next-line compat/compat
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    [liquidNobleApi, activeEntityId],
  );

  return { downloadCgtReport };
};
